// src/Relatorios.js

import React, { useState } from 'react';
import { db } from './firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';

// Imports dos componentes MUI
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Tooltip, IconButton,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';

// Imports para o seletor de data
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

const formatarData = (dataStr) => {
    if (!dataStr) return 'N/A';
    const p = dataStr.split('-');
    return p.length !== 3 ? dataStr : `${p[2]}/${p[1]}/${p[0]}`;
};

const formatarMoeda = (valor) => {
    if (typeof valor !== 'number') { valor = 0; }
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const Relatorios = () => {
  dayjs.locale('pt-br');
  const [dataInicio, setDataInicio] = useState(dayjs().startOf('month'));
  const [dataFim, setDataFim] = useState(dayjs().endOf('month'));
  const [dadosRelatorio, setDadosRelatorio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [relatorioGerado, setRelatorioGerado] = useState(false);
  const [tipoRelatorio, setTipoRelatorio] = useState('reservas');

  const handleGerarRelatorio = async () => {
    if (!dataInicio || !dataFim || dataFim.isBefore(dataInicio)) {
      toast.error("Por favor, selecione um período de datas válido.");
      return;
    }
    setLoading(true);
    setRelatorioGerado(false);
    try {
      const dataInicioFormatada = dataInicio.format('YYYY-MM-DD');
      const dataFimFormatada = dataFim.format('YYYY-MM-DD');
      
      const reservasRef = collection(db, 'reservas');

      // Busca reservas onde o checkout é maior ou igual à data de inicio
      const q = query(
        reservasRef,
        where('dataCheckout', '>=', dataInicioFormatada)
      );
      
      const querySnapshot = await getDocs(q);
      const todasAsReservas = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filtra na memória para garantir que o checkin seja menor ou igual à data fim
      const reservasFiltradas = todasAsReservas.filter(reserva => 
        reserva.dataCheckin <= dataFimFormatada
      );

      // --- LÓGICA DE ORDENAÇÃO ATUALIZADA ---
      reservasFiltradas.sort((a, b) => {
        // 1. Primeiro compara as datas de Check-in
        const comparacaoData = a.dataCheckin.localeCompare(b.dataCheckin);
        
        // Se as datas forem diferentes, retorna a ordenação por data
        if (comparacaoData !== 0) {
            return comparacaoData;
        }

        // 2. Se as datas forem iguais, ordena pelo nome do Chalé
        // A opção { numeric: true } garante que "Chalé 2" venha antes de "Chalé 10"
        const nomeA = a.chaleNome || '';
        const nomeB = b.chaleNome || '';
        
        return nomeA.localeCompare(nomeB, undefined, { numeric: true, sensitivity: 'base' });
      });
      // --- FIM DA LÓGICA DE ORDENAÇÃO ---

      setDadosRelatorio(reservasFiltradas);
      setRelatorioGerado(true);

      if (reservasFiltradas.length === 0) {
        toast.info("Nenhuma reserva encontrada para o período selecionado.");
      }
    } catch (error) {
      console.error("Erro ao gerar relatório: ", error);
      toast.error("Ocorreu um erro ao buscar os dados do relatório.");
    } finally {
      setLoading(false);
    }
  };

  const exportarParaCSV = () => {
    if (dadosRelatorio.length === 0) {
      toast.warn("Não há dados para exportar.");
      return;
    }

    let cabecalho;
    let linhas;
    let nomeArquivo;

    if (tipoRelatorio === 'portaria') {
        cabecalho = ["Chale", "Check-in", "Check-out", "Hospede Titular", "Acompanhantes"];
        nomeArquivo = `relatorio_portaria_${dataInicio.format('YYYY-MM-DD')}_a_${dataFim.format('YYYY-MM-DD')}.csv`;
        linhas = dadosRelatorio.map(reserva => {
            const acompanhantes = (reserva.acompanhantes || []).map(a => a.nome).join(', ');
            return [
                reserva.chaleNome,
                formatarData(reserva.dataCheckin),
                formatarData(reserva.dataCheckout),
                `"${reserva.hospedeNome}"`,
                `"${acompanhantes}"`
            ].join(';');
        });
    } else { // Relatório de Reservas
        cabecalho = [
          "Voucher", "Hospede", "Chale", "Checkin", "Checkout",
          "Noites", "Status", "Valor Total", "Adiantamento", "Pagamento Final", "Desconto", "Saldo Devedor"
        ];
        nomeArquivo = `relatorio_reservas_${dataInicio.format('YYYY-MM-DD')}_a_${dataFim.format('YYYY-MM-DD')}.csv`;
        linhas = dadosRelatorio.map(reserva => {
          const valorTotal = reserva.valorTotal || 0;
          const adiantamento = reserva.adiantamento?.valor || 0;
          const pagFinal = reserva.pagamentoRestante?.valor || 0;
          const desconto = reserva.desconto || 0;
          const saldo = valorTotal - adiantamento - pagFinal - desconto;
    
          return [
            reserva.codigoVoucher, `"${reserva.hospedeNome}"`, reserva.chaleNome,
            formatarData(reserva.dataCheckin), formatarData(reserva.dataCheckout),
            reserva.numDiarias || 0, reserva.statusReserva,
            valorTotal.toFixed(2).replace('.',','), adiantamento.toFixed(2).replace('.',','),
            pagFinal.toFixed(2).replace('.',','), desconto.toFixed(2).replace('.',','),
            saldo.toFixed(2).replace('.',',')
          ].join(';');
        });
    }

    const csvContent = "data:text/csv;charset=utf-8," + cabecalho.join(';') + "\n" + linhas.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", nomeArquivo);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleImprimir = () => { window.print(); };

  const TabelaRelatorio = () => {
    if (tipoRelatorio === 'portaria') {
        return (
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="tabela da portaria" size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Chalé</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Período</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Hóspedes</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dadosRelatorio.map((reserva) => (
                            <TableRow key={reserva.id} hover>
                                <TableCell>{reserva.chaleNome}</TableCell>
                                <TableCell>{formatarData(reserva.dataCheckin)} a {formatarData(reserva.dataCheckout)}</TableCell>
                                <TableCell>
                                    <Typography variant="body2" component="div" fontWeight="500">{reserva.hospedeNome} (Titular)</Typography>
                                    {(reserva.acompanhantes || []).filter(a => a.nome).map((acomp, index) => (
                                        <Typography key={index} variant="caption" component="div" sx={{ pl: 1 }}>- {acomp.nome}</Typography>
                                    ))}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }
    return (
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="tabela de relatório de reservas" size="small">
            <TableHead>
                <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Hóspede</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Chalé</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Período</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor Total</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {dadosRelatorio.map((reserva) => (
                <TableRow key={reserva.id} hover>
                    <TableCell>
                    <Typography variant="body2" fontWeight="500">{reserva.hospedeNome}</Typography>
                    <Typography variant="caption" color="text.secondary">Voucher: {reserva.codigoVoucher}</Typography>
                    </TableCell>
                    <TableCell>{reserva.chaleNome}</TableCell>
                    <TableCell>{formatarData(reserva.dataCheckin)} a {formatarData(reserva.dataCheckout)}</TableCell>
                    <TableCell>{reserva.statusReserva}</TableCell>
                    <TableCell align="right">{formatarMoeda(reserva.valorTotal)}</TableCell>
                </TableRow>
                ))}
            </TableBody>
            </Table>
        </TableContainer>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
       <style>{`@media print { body * { visibility: hidden; } .printable-area, .printable-area * { visibility: visible; } .printable-area { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; } }`}</style>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 2, mb: 3 }} className="no-print">
          <Typography variant="h5" component="h1" fontWeight="600">
            Emissão de Relatórios
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{minWidth: 240}}>
                <InputLabel>Tipo de Relatório</InputLabel>
                <Select
                    value={tipoRelatorio}
                    label="Tipo de Relatório"
                    onChange={(e) => setTipoRelatorio(e.target.value)}
                >
                    <MenuItem value="reservas">Reservas (Completo)</MenuItem>
                    <MenuItem value="portaria">Portaria (Simplificado)</MenuItem>
                </Select>
            </FormControl>

            <DatePicker
              label="Data de Início"
              value={dataInicio}
              onChange={(novaData) => setDataInicio(novaData)}
            />
            <DatePicker
              label="Data de Fim"
              value={dataFim}
              onChange={(novaData) => setDataFim(novaData)}
            />
            <Button
              variant="contained"
              startIcon={<AssessmentIcon />}
              onClick={handleGerarRelatorio}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Gerar Relatório'}
            </Button>
            {relatorioGerado && dadosRelatorio.length > 0 && (
                 <>
                    <Tooltip title="Exportar para Excel (CSV)">
                        <IconButton color="primary" onClick={exportarParaCSV}>
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Imprimir Relatório">
                        <IconButton color="secondary" onClick={handleImprimir}>
                            <PrintIcon />
                        </IconButton>
                    </Tooltip>
                 </>
            )}
          </Box>
        </Paper>

        {relatorioGerado && (
          <Box className="printable-area">
            <Typography variant="h6" gutterBottom>
                {tipoRelatorio === 'reservas' ? 'Relatório de Reservas' : 'Relatório da Portaria'} - {formatarData(dataInicio.format('YYYY-MM-DD'))} a {formatarData(dataFim.format('YYYY-MM-DD'))}
            </Typography>
            <TabelaRelatorio />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default Relatorios;